import React, { useEffect } from "react";
import { MapContainer, TileLayer, Circle, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Color-coded pin per audit status. We build divIcons inline so we don't have
// to ship per-color PNGs and the bundle stays small.
function buildIcon(status, isFocused) {
  const fill =
    status === "COMPLETED" ? "#059669" : status === "QUEUED" ? "#f59e0b" : "#3f3f46";
  const ring = isFocused ? "#2563eb" : "#0c0c0c";
  const size = isFocused ? 30 : 24;
  const html = `
    <div style="
      width:${size}px;height:${size}px;border-radius:50% 50% 50% 0;
      background:${fill};border:2px solid ${ring};
      transform:rotate(-45deg);
      box-shadow:0 1px 4px rgba(0,0,0,0.4);
      display:flex;align-items:center;justify-content:center;
    ">
      <div style="
        width:8px;height:8px;border-radius:50%;
        background:#ffffff;transform:rotate(45deg);
      "></div>
    </div>`;
  return L.divIcon({
    html,
    className: "geo-pin",
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    popupAnchor: [0, -size],
  });
}

// Recenter helper — when scan center changes (new scan), fit bounds to all
// pins + circle so the operator immediately sees the whole AOI.
function MapRecenter({ center, radiusMeters, leads, focusId }) {
  const map = useMap();
  useEffect(() => {
    if (!center) return;
    if (focusId) {
      const lead = leads.find((l) => l.id === focusId);
      if (lead?.latitude != null && lead?.longitude != null) {
        map.flyTo([lead.latitude, lead.longitude], 16, { duration: 0.6 });
        return;
      }
    }
    const points = leads
      .filter((l) => l.latitude != null && l.longitude != null)
      .map((l) => [l.latitude, l.longitude]);
    points.push(center);
    if (points.length <= 1) {
      map.setView(center, 14);
    } else {
      const bounds = L.latLngBounds(points);
      map.fitBounds(bounds.pad(0.2), { maxZoom: 15 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center?.[0], center?.[1], radiusMeters, leads.length, focusId]);
  return null;
}

export default function GeoMap({
  centerLat,
  centerLng,
  radiusMiles,
  leads = [],
  focusId = null,
  onPinClick,
  onAuditPin,
}) {
  const haveCenter = Number.isFinite(centerLat) && Number.isFinite(centerLng);
  const fallback = [40.7128, -74.006]; // NYC fallback when no scan yet
  const center = haveCenter ? [centerLat, centerLng] : fallback;
  const radiusMeters = (radiusMiles || 2) * 1609.34;

  return (
    <div
      className="border-2 border-zinc-950 overflow-hidden mb-4"
      style={{ height: 420 }}
      data-testid="geo-leaflet-map"
    >
      <MapContainer
        center={center}
        zoom={haveCenter ? 13 : 11}
        scrollWheelZoom={true}
        style={{ height: "100%", width: "100%" }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {haveCenter && (
          <>
            <Circle
              center={center}
              radius={radiusMeters}
              pathOptions={{
                color: "#2563eb",
                weight: 2,
                fillColor: "#2563eb",
                fillOpacity: 0.08,
                dashArray: "6 4",
              }}
            />
            <Marker
              position={center}
              icon={L.divIcon({
                html: `<div style="width:18px;height:18px;border-radius:50%;background:#2563eb;border:3px solid #fff;box-shadow:0 0 0 2px #2563eb;"></div>`,
                className: "geo-center-pin",
                iconSize: [18, 18],
                iconAnchor: [9, 9],
              })}
            >
              <Popup>
                <div style={{ fontSize: 12, fontFamily: "monospace" }}>
                  Scan center<br />
                  {centerLat.toFixed(4)}, {centerLng.toFixed(4)}<br />
                  Radius {radiusMiles} mi
                </div>
              </Popup>
            </Marker>
          </>
        )}
        {leads.map((l) => {
          if (l.latitude == null || l.longitude == null) return null;
          return (
            <Marker
              key={l.id}
              position={[l.latitude, l.longitude]}
              icon={buildIcon(l.audit_status, focusId === l.id)}
              eventHandlers={{
                click: () => onPinClick && onPinClick(l.id),
              }}
            >
              <Popup>
                <div style={{ minWidth: 180, fontSize: 12 }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }} data-testid={`geo-pin-name-${l.id}`}>
                    {l.business_name}
                  </div>
                  {l.business_category && (
                    <div style={{ textTransform: "uppercase", fontSize: 10, color: "#737373", letterSpacing: 1 }}>
                      {l.business_category}
                    </div>
                  )}
                  {l.street_address && (
                    <div style={{ fontSize: 11, marginTop: 4 }}>{l.street_address}</div>
                  )}
                  {l.website_url && (
                    <a
                      href={l.website_url}
                      target="_blank"
                      rel="noreferrer"
                      style={{ fontSize: 11, color: "#1d4ed8", display: "block", marginTop: 4 }}
                    >
                      {new URL(l.website_url).host.replace("www.", "")}
                    </a>
                  )}
                  <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 6 }}>
                    <span
                      style={{
                        background:
                          l.audit_status === "COMPLETED"
                            ? "#059669"
                            : l.audit_status === "QUEUED"
                            ? "#f59e0b"
                            : "#a1a1aa",
                        color: l.audit_status === "QUEUED" ? "#18181b" : "#fff",
                        fontSize: 9,
                        fontWeight: 700,
                        letterSpacing: 1,
                        textTransform: "uppercase",
                        padding: "2px 6px",
                      }}
                    >
                      {l.audit_status}
                    </span>
                    {l.website_url &&
                      l.audit_status !== "QUEUED" &&
                      l.audit_status !== "COMPLETED" && (
                        <button
                          type="button"
                          onClick={() => onAuditPin && onAuditPin(l)}
                          data-testid={`geo-pin-audit-${l.id}`}
                          style={{
                            background: "#1d4ed8",
                            color: "#fff",
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: 1,
                            textTransform: "uppercase",
                            padding: "3px 8px",
                            border: 0,
                            cursor: "pointer",
                          }}
                        >
                          Audit
                        </button>
                      )}
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}
        <MapRecenter
          center={haveCenter ? center : null}
          radiusMeters={radiusMeters}
          leads={leads}
          focusId={focusId}
        />
      </MapContainer>
    </div>
  );
}
